import React, { memo, useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
// Import Types
import type { PageSizeType } from 'hooks/useListBaseRequestBody';
import type { Sorting } from 'types';
// Import UI components
import { Button } from '@geneui/components';
// Import Components
import { DataTable } from 'components';
import { AdminSMSProviderActionsButtons } from 'components/AdminSMSProvider/AdminSMSProvidersList';
// Import Hooks
import { useGoToRoute, useHasPermission, useListBaseRequestBody, useQueryParams, useRequest } from 'hooks';
// Import Services
import { AdministrationHttpService } from 'services/http';
// Import Configs
import { DataTableColumnsConfigs, getTableRows } from './config';
// Import Constants
import { l, PERMISSIONS } from 'constants/common';
import { RoutesList } from 'routes';

const { ADMINISTRATION_CREATE_SMS_PROVIDER, ADMINISTRATION_EDIT_SMS_PROVIDER, ADMINISTRATION_CLONE_SMS_PROVIDER } =
    RoutesList;

interface Props {
    tableKey?: string;
    listAction: {
        setAdminSMSProviderTableFilters: () => void;
        setAdminSMSProviderTablePageNumber: (pageNumber: number) => void;
        setAdminSMSProviderTablePageSize: (pageSize: PageSizeType) => void;
        setAdminSMSProviderTableSorting: (sorting: Sorting) => void;
        resetAdminSMSProviderState: () => void;
    };
    listSelectorKey: string;
}

interface DataTableRef {
    resetSelectedRows?: () => void;
    reset?: () => void;
}

const AdminSMSProvidersList = forwardRef<DataTableRef | null, Props>(
    ({ tableKey = 'adminProvidersDataTable', listAction, listSelectorKey }, ref) => {
        const { t } = useTranslation();
        const { search } = useLocation();
        const { goToRoute } = useGoToRoute();
        const goToRouteQueryParams = useQueryParams();
        const { hasPermission } = useHasPermission();
        const hasAdminPermission = !!hasPermission(PERMISSIONS.CRMAdmin);

        const dispatch = useDispatch();
        const { doPostRequest } = useRequest();
        const { tableData } = useSelector((state) => state[listSelectorKey]);

        const { filters, paging, sorting } = tableData;

        const {
            baseRequestBody,
            setFilters: setBaseRequestBodyFilters,
            setPagingPageNumber,
            setPagingPageSize,
            setSorting,
        } = useListBaseRequestBody(filters, paging, sorting);

        const {
            resetAdminSMSProviderState,
            setAdminSMSProviderTablePageNumber,
            setAdminSMSProviderTablePageSize,
            setAdminSMSProviderTableSorting,
        } = listAction;

        const [isProviderListLoading, setIsProviderListLoading] = useState(false);
        const [providerListFilters, setProviderListFilters] = useState(baseRequestBody);

        const [providerListData, setProviderListData] = useState([]);
        const [providerListDataCount, setProviderListDataCount] = useState(0);

        const { columns } = DataTableColumnsConfigs(t);
        const isInit = useRef(false);
        const dataTableRef = useRef<DataTableRef | null>();

        const { getSMSProvidersListRequest } = useMemo(
            () => ({
                getSMSProvidersListRequest: AdministrationHttpService.getSMSProvidersList(),
            }),
            [],
        );

        const listSorting = useMemo(
            () => ({
                name: providerListFilters.Sorting?.Name,
                direction: providerListFilters.Sorting?.Direction,
            }),
            [providerListFilters.Sorting],
        );

        const getProviderList = useCallback(
            (filter = providerListFilters) => {
                setIsProviderListLoading(true);
                doPostRequest(getSMSProvidersListRequest.request, {
                    requestBody: filter,
                    successCallback: ({ Data, Count }) => {
                        const rows = getTableRows(Data || []);
                        setProviderListData(rows);
                        setProviderListDataCount(Count);
                    },
                })?.then(() => setIsProviderListLoading(false));
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [providerListFilters],
        );

        const getHeaderActions = () => {
            return (
                <Button
                    flexibility="default"
                    icon="bc-icon-profit"
                    onClick={(e) => {
                        e.preventDefault();
                        goToRoute(
                            ADMINISTRATION_CREATE_SMS_PROVIDER,
                            undefined,
                            goToRouteQueryParams.encode({ backParams: search }),
                        );
                    }}
                    disabled={!hasAdminPermission}
                >
                    {t(l.CreateProvider)}
                </Button>
            );
        };

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const goToActionPage = (route: any) => (provider: Record<string, any>) => {
            goToRoute(
                route,
                { SmsProviderId: provider.SmsProviderId },
                goToRouteQueryParams.encode({ backParams: search }),
            );
        };

        const getRowActionBar = (row) => {
            const { data } = row;

            return (
                <>
                    <AdminSMSProviderActionsButtons
                        data={data}
                        editProviderAction={goToActionPage(ADMINISTRATION_EDIT_SMS_PROVIDER)}
                        cloneProviderAction={goToActionPage(ADMINISTRATION_CLONE_SMS_PROVIDER)}
                    />
                </>
            );
        };

        // Data table part
        const setPagingPageNumberHandler = (pageNumber: number) => {
            setPagingPageNumber(pageNumber);
            dispatch(setAdminSMSProviderTablePageNumber(pageNumber));
            setProviderListFilters(baseRequestBody);
        };

        const setPagingPageSizeHandler = (pageSize) => {
            setPagingPageSize(pageSize);
            dispatch(setAdminSMSProviderTablePageSize(pageSize));
            setProviderListFilters(baseRequestBody);
        };

        const setSortingHandler = (sortingDirection, columnName) => {
            setSorting(sortingDirection, columnName);
            dispatch(setAdminSMSProviderTableSorting(baseRequestBody.Sorting));
            setProviderListFilters(baseRequestBody);
        };

        const getTableRefreshFn = () => {
            getProviderList(providerListFilters);
        };

        useImperativeHandle(ref, () => ({
            resetSelectedRows() {
                dataTableRef.current?.resetSelectedRows?.();
            },
            reset() {
                dataTableRef.current?.reset?.();
            },
        }));

        const cleanUp = () => {
            return () => {
                // Cancel all async processes
                getSMSProvidersListRequest.cancel('SMSProvidersListPage:getSMSProvidersListRequest');
                // Reset store state
                resetAdminSMSProviderState && dispatch(resetAdminSMSProviderState());
            };
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(cleanUp, []);

        useEffect(() => {
            if (isInit.current) {
                getProviderList(providerListFilters);
            }
            /* eslint-disable react-hooks/exhaustive-deps */
        }, [providerListFilters]);

        useEffect(() => {
            setBaseRequestBodyFilters(filters);
            setProviderListFilters(baseRequestBody);

            isInit.current = true;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [filters]);

        return (
            <DataTable
                classNames="with-filter-tags-and-page-headline-height"
                name={l.SMSProviders}
                rowKey="rowKey"
                columnKey="dataKey"
                tableKey={tableKey}
                isColumnsSortable={true}
                data={providerListData}
                dataCount={providerListDataCount}
                rowActionBar={getRowActionBar}
                columns={columns}
                isLoading={isProviderListLoading}
                isShowRefreshButton={true}
                isShowColumnChooser={true}
                setPagingPageSize={setPagingPageSizeHandler}
                setPagingPageNumber={setPagingPageNumberHandler}
                sorting={listSorting}
                setSorting={setSortingHandler}
                onRefreshClick={getTableRefreshFn}
                headerActions={getHeaderActions()}
                isHasArchiveActions={false}
                isHasUnarchiveActions={false}
                recevedCurrentPageNumber={providerListFilters.Pageing.PageNumber}
                withQueryParams={true}
                ref={dataTableRef}
            />
        );
    },
);

AdminSMSProvidersList.displayName = 'AdminProviderList';

export default memo(AdminSMSProvidersList);
